import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/lib/provider/AuthProvider'
import { CREATE_COMPANY_PATH } from '@/lib/hooks/useRequireCompany'
import { useTenant } from '@/lib/provider/TenantProvider'
import { cn } from '@/lib/misc/Utils'

export const COMPANY_PROFILE_PATH = '/settings/company-profile'

/**
 * The organization the navigation should name. While a customer's portal is
 * being viewed, that is the customer — not the signed-in user's own company,
 * which stays put for the whole impersonation.
 */
export const SET_UP_COMPANY_LABEL = 'Set up company'

export const useOrganizationName = (): string => {
  const [auth] = useAuth()
  const { customerId, tenantName } = useTenant()

  // The tenant's profile is fetched, so its name is briefly unknown. Falling
  // back to the user's own company there would name the wrong organization.
  if (customerId) return tenantName ?? '…'

  return auth.user.company?.name ?? SET_UP_COMPANY_LABEL
}

interface OrganizationButtonProps {
  className?: string
  children?: (organizationName: string) => React.ReactNode
}

const OrganizationButton = ({
  className,
  children,
}: OrganizationButtonProps) => {
  const [auth] = useAuth()
  const navigate = useNavigate()

  const organizationName = useOrganizationName()

  const hasCompany = Boolean(auth.user?.customer_id)

  const onActivate = useCallback(() => {
    navigate(hasCompany ? COMPANY_PROFILE_PATH : CREATE_COMPANY_PATH)
  }, [hasCompany, navigate])

  return (
    <button
      type="button"
      data-cy="organization-button"
      onClick={onActivate}
      title={
        hasCompany ? `${organizationName} — company profile` : organizationName
      }
      className={cn(className)}
    >
      {children ? (
        children(organizationName)
      ) : (
        <span className="max-w-[140px] truncate">{organizationName}</span>
      )}
    </button>
  )
}

export default OrganizationButton
